import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import FormLabel from '../FormLabel';
import ButtonGroup from '../ButtonGroup';
import Button from '../Button';
import Icon from '../Icon';
import useTheme from '../../hooks/useTheme';
import { createPreview } from '../../utils/helpers';
import FormError from '../FormError';
import { StyledImageUpload, StyledImageUploadWrapper, StyledImage, StyledInputWrapper, StyledInput } from './styled';
import { makeStyles } from '../../utils/styles';
import Box from '../Box';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((css) => ({
    icon: css`
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
        pointer-events: none;
    `,
}));

const ImageUpload = ({ id, label, name, error, disabled, readonly, image, errorText, customStyle, onDrop, imageURL }) => {
    const { t } = useTranslation()
    const styles = useStyles();
    const theme = useTheme();

    const handleDrop = useCallback(acceptedFiles => {
        onDrop(name, acceptedFiles[0]);
    }, [name, onDrop]);

    const handleDelete = useCallback(() => {
        onDrop(name, null);
    }, [name, onDrop]);

    const { getRootProps, getInputProps, open } = useDropzone({
        accept: "image/jpeg,image/png,image/jpg",
        maxFiles: 1,
        onDrop: handleDrop
    });

    return (
        <StyledImageUpload
            disabled={disabled}
            readonly={readonly}
            error={error}
            customStyle={customStyle}
        >
            {label && <FormLabel htmlFor={id}>{label}</FormLabel>}
            <StyledImageUploadWrapper>
                <StyledImage
                    imgPreview={image ? (typeof image === 'string') ? image : createPreview(image) : imageURL}
                >
                    <StyledInputWrapper {...getRootProps()}>
                        <StyledInput
                            id={id}
                            name={name}
                            {...getInputProps()}
                        />
                    </StyledInputWrapper>
                    {
                        (!!!image && !!!imageURL) && (
                            <Box customStyle={styles.icon}>
                                <Icon
                                    name="object"
                                    width={72}
                                    height={72}
                                    fill={theme.colors.white}
                                />
                            </Box>
                        )
                    }
                </StyledImage>
                {
                    !readonly && (
                        image ? (
                            <ButtonGroup
                                direction="column"
                            >
                                <Button
                                    onClick={handleDelete}
                                    startIcon={<Icon name="delete" fill={theme.colors.purple} width="12" height="12" />}
                                    type="button"
                                    variant="text"
                                >
                                    {t("USUŃ LOGO HOTELU")}
                            </Button>
                                <Button
                                    onClick={open}
                                    startIcon={<Icon name="edit" fill={theme.colors.purple} width="12" height="12" />}
                                    type="button"
                                    variant="text"
                                >
                                    {t("EDYTUJ LOGO HOTELU")}
                            </Button>
                            </ButtonGroup>
                        ) : (
                                <Button
                                    onClick={open}
                                    startIcon={<Icon name="add" fill={theme.colors.purple} width="12" height="12" />}
                                    type="button"
                                    variant="text"
                                >
                                    {t("DODAJ LOGO HOTELU")}
                                </Button>
                            )
                    )
                }
            </StyledImageUploadWrapper>
            {error && errorText && <FormError>{errorText}</FormError>}
        </StyledImageUpload>
    );
};

ImageUpload.propTypes = {
    id: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    image: PropTypes.any,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    readonly: PropTypes.bool,
    errorText: PropTypes.string,
    customStyle: PropTypes.array,
    onDrop: PropTypes.func,
    onClickDelete: PropTypes.func,
    imageURL: PropTypes.string
};

ImageUpload.defaultProps = {
    image: null,
    error: false,
    disabled: false,
    readonly: false,
    onDrop: undefined,
    onClickDelete: undefined
};

export default React.memo(ImageUpload);
